import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Activity, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import Seo from '../components/Seo';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true for better UX
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Show session expired message if redirected
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      toast.info('Your session has expired. Please sign in again.', { duration: 5000 });
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password, rememberMe);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-4 sm:py-8 flex flex-col justify-center">
      <Seo
        title="Sign In – MyCBC | CLL Blood Cancer Companion"
        description="Sign in to MyCBC to track your CLL/SLL blood results, symptoms, treatments and nutrition."
        path="/login"
      />
      {/* Back navigation - always visible */}
      <div className="w-full max-w-md mx-auto mb-3 sm:mb-6">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary transition-colors text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to website
        </Link>
      </div>

      <div className="w-full max-w-md mx-auto">
        {/* Clickable logo/branding - returns to homepage */}
        <Link to="/" className="block text-center mb-4 sm:mb-8 group">
          <Activity className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 mx-auto mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Welcome Back</h1>
          <p className="text-sm sm:text-base text-slate-600">Sign in to your Blood Cancer Companion account</p>
        </Link>
        
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-11 sm:h-12 pl-10 sm:pl-12 pr-4 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 sm:h-12 pl-10 sm:pl-12 pr-12 rounded-xl border border-slate-200 bg-white text-base sm:text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember me checkbox */}
            <label htmlFor="remember-me" className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-5 w-5 rounded border-2 border-slate-400 bg-white text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary transition-colors"
                  style={{ 
                    appearance: 'auto',
                    WebkitAppearance: 'checkbox',
                    minWidth: '20px',
                    minHeight: '20px'
                  }}
                />
              </div>
              <div>
                <span className="block text-sm font-medium text-slate-700 group-hover:text-slate-900">Remember me</span>
                <span className="block text-xs text-slate-500">Stay signed in on this device</span>
              </div>
            </label>
            
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          
          <div className="mt-4 sm:mt-6 text-center space-y-2 sm:space-y-3">
            <Link to="/forgot-password" className="text-primary font-medium hover:underline block text-sm sm:text-base">
              Forgot your password?
            </Link>
            <p className="text-slate-600 text-sm sm:text-base">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;